import z from "zod";
import { ZodDateString } from "../zod-types/zod-date-string";
import { ZodStringNullable } from "../zod-types/zod-string-nullable";

export const crewDefinitionSchema = z
	.object({
		name: ZodStringNullable,
		/**
		 * Whose crew this is: the caller's own organisation, or a partner it hosts. The handler is
		 * what decides which of those it is, because only the request knows the caller's own id —
		 * it stores `partnerOrgId` only when this names somebody else.
		 */
		organizationId: z.string().min(1),
		vehicleId: ZodStringNullable,
		startDate: ZodDateString,
		endDate: ZodStringNullable,
		/**
		 * The weekdays this crew turns up on, Monday-first offsets. Mandatory and never empty: a
		 * crew working no day of the week could not be scheduled onto anything, and the column's
		 * own check says the same.
		 */
		weekdays: z.array(z.number().int().min(0).max(6)).min(1),
		leadUserId: ZodStringNullable,
		memberUserIds: z.array(z.string().min(1)),
	})
	.superRefine((values, ctx) => {
		// The span's own check, mirrored from `crew_definitions_end_date_after_start_date_check` so
		// a form answers it instead of a 500.
		if (values.endDate && values.endDate < values.startDate) {
			ctx.addIssue({
				code: "custom",
				message: "endDate must not be before startDate",
				path: ["endDate"],
			});
		}

		// The foreman is picked in its own field and filtered out of the member list, so the same
		// person arriving through both is a defect rather than a choice —
		// `unique_member_per_crew_definition` would refuse the second row.
		if (values.leadUserId && values.memberUserIds.includes(values.leadUserId)) {
			ctx.addIssue({
				code: "custom",
				message: "The foreman is already a member of this crew",
				path: ["leadUserId"],
			});
		}
	});

export type CrewDefinitionFormSchema = z.infer<typeof crewDefinitionSchema>;
